import { Fade, Stack } from '@mui/material'
import { WHITE } from 'constants/styling/theme'

import { AnalyticsEvent } from 'utils/analytics'
import { Fragment } from 'react'
import { MUIDivider } from 'components/common/MUIDivider'
import { SectionWrapped } from '../TopBarDropdown/TopBarDropdownSectionItem.component'
import { TopBarDropdownMobileClientContent } from './TopBarDropdownMobileClientContent.component'
import { TopBarDropdownMobileInternalContent } from './TopBarDropdownMobileInternalContent.component'
import { TopBarNavigation } from '../TopBarNavigation'
import { WorkspaceDropdownMobile } from '../WorkspaceDropdownMobile'
import { useAuth0 } from 'utils/auth'
import { useIsRegistrationFlow } from 'utils/hooks'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @interface Props for the TopBarDropdownMobile component.
 */
export interface Props {
  /** Whether the menu is open or not */
  showMenu: boolean
  /** Function to set the language open */
  setIsLanguageOpen: () => void
  /** Function to set the contact us open */
  setIsContactUsOpen: () => void
  /** Function to close the menu */
  closeMenuHandler: (e: React.MouseEvent<Element, MouseEvent>) => void
  /** Function to handle the logout */
  handleLogout: (e: React.MouseEvent<Element, MouseEvent>) => void
  /** Function to handle the analytics event */
  handleLogAnalyticsEvent: (event: AnalyticsEvent, options?: {}) => void
  /** Callback to open the property selection drawer */
  onOpenPropertyDrawer: () => void
  /** Starts a new order from the menu. Owned by the TopBar, so the dialog it opens survives the menu closing. */
  onStartNewOrder: () => void
}

/**
 * @component TopBarDropdownMobile displayed TopBar
 * @example <TopBarDropdownMobile />
 */
export const TopBarDropdownMobile: React.FC<Props> = ({ showMenu, setIsLanguageOpen, setIsContactUsOpen, closeMenuHandler, handleLogout, handleLogAnalyticsEvent, onOpenPropertyDrawer, onStartNewOrder }) => {
  const { t } = useTranslation(['workspace_profile'])

  const { isAuthenticated, loading, user, roles } = useAuth0()
  const isRegistrationFlow = useIsRegistrationFlow()

  const { currentUserWorkspace } = useUserData()

  const contentProps = { setIsLanguageOpen, setIsContactUsOpen, closeMenuHandler, handleLogout, handleLogAnalyticsEvent, onOpenPropertyDrawer, onStartNewOrder }

  return (
    <Fragment>
      {showMenu && (
        <Fade in={showMenu} easing="easing">
          <div className="menu-wrap">
            <Stack
              sx={{
                position: 'fixed',
                top: '6.4rem',
                right: 0,
                bottom: 0,
                left: 0,
                height: 'calc(100vh - 6.4rem)',
                width: '100vw',
                zIndex: 50,
                display: 'flex',
              }}
            >
              <Stack
                sx={{
                  boxSizing: 'border-box',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  background: WHITE,
                  fontWeight: 600,
                  lineHeight: '140%',
                  width: '100%',
                  height: '100%',
                  overflowY: 'auto',
                  padding: '1.6rem 1.6rem 4.8rem 1.6rem',
                  '.section-item-label': {
                    fontSize: '1.6rem !important',
                  },
                }}
              >
                {!loading && isAuthenticated && user && (
                  <Fragment>
                    {!isRegistrationFlow && (!!currentUserWorkspace || roles.isClient) && (
                      <Stack width="100%">
                        <SectionWrapped>
                          {roles.isClient && <WorkspaceDropdownMobile onCloseMenu={closeMenuHandler} handleLogAnalyticsEvent={handleLogAnalyticsEvent} />}
                        </SectionWrapped>

                        <MUIDivider margin={16} />
                      </Stack>
                    )}

                    <TopBarNavigation isMobile onCloseMenu={closeMenuHandler} />

                    {roles.isClient && <TopBarDropdownMobileClientContent {...contentProps} />}
                    {!roles.isClient && <TopBarDropdownMobileInternalContent {...contentProps} />}
                  </Fragment>
                )}
              </Stack>
            </Stack>
          </div>
        </Fade>
      )}
    </Fragment>
  )
}
